import { ChangeDetectionStrategy, Component, computed, effect, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FloatingPopupTabsComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-tabs/floating-popup-tabs.component';
import { FloatingPopupBaseContentComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-base-content/floating-popup-base-content.component';
import { FLOATING_POPUP_DATA } from '@app/main/main-content/floating-popup-manager/providers/floating-popup-data.token';
import { FloatingPopupTabsService } from '@app/main/main-content/floating-popup-manager/services/floating-popup-tabs.service';
import { FpLoggerNoiseTabs } from '../../enums/fp-logger-noise-tabs.enum';
import { ButtonModule } from 'primeng/button';
import { SvgIconComponent } from 'angular-svg-icon';
import { TranslatePipe } from '@ngx-translate/core';
import { Tooltip } from 'primeng/tooltip';
import { ScreenResolutionService } from '@app/core/services/screen-resolution/screen-resolution.service';
import { FpLoggerNoiseManagerStore } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/services/fp-logger-noise-manager/fp-logger-noise-manager.store';
import { toSignal } from '@angular/core/rxjs-interop';
import { FpLoggerNoiseManagerActionType } from '@app/main/leak-detection/floating-popups/floating-popup-logger-noise/services/fp-logger-noise-manager/fp-logger-noise-manager.actions';
import { SpinnerComponent } from '@app/shared/components/spinner/spinner.component';
import { ILdLogger } from '@app/main/leak-detection/interfaces/ld-logger';
import { FloatingPopupBaseErrorMessageComponent } from '@app/main/main-content/floating-popup-manager/components/floating-popup-base-error-message/floating-popup-base-error-message.component';
import { DialogTrackerService } from '@app/core/services/dialog-tracker/dialog-tracker.service';
import {
  DialogCreateLoggerNoiseReportComponent,
  IDialogCreateLoggerNoiseReportData,
} from '@app/main/leak-detection/dialogs/dialog-create-logger-noise-report/dialog-create-logger-noise-report.component';

@Component({
  selector: 'gc-fp-logger-noise-content',
  standalone: true,
  imports: [
    CommonModule,
    FloatingPopupTabsComponent,
    ButtonModule,
    SvgIconComponent,
    TranslatePipe,
    Tooltip,
    SpinnerComponent,
    FloatingPopupBaseErrorMessageComponent,
  ],
  templateUrl: './fp-logger-noise-content.component.html',
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FpLoggerNoiseContentComponent extends FloatingPopupBaseContentComponent {
  public readonly data = inject<ILdLogger>(FLOATING_POPUP_DATA);
  public readonly scrRes = inject(ScreenResolutionService);
  private readonly floatingPopupTabs = inject(FloatingPopupTabsService);
  private readonly floatingPopupManagerStore = inject(FpLoggerNoiseManagerStore);
  private readonly dialogTrackerService = inject(DialogTrackerService);

  public selectedTab = computed(() => this.floatingPopupTabs.getActive(this.popupId())());

  public tabs = computed(() => this.service.prepareFormControlConfigForPopupTab(this.popupId()), {
    equal: (a, b) => a?.id === b?.id,
  });

  public activeTabConnectService = toSignal(this.floatingPopupManagerStore.activeTabConnectService$, {
    initialValue: null,
  });

  public loading = toSignal(this.floatingPopupManagerStore.loading$, {
    initialValue: false,
  });

  public selectedData = toSignal(this.floatingPopupManagerStore.selectedData$, {
    initialValue: null,
  });

  public initialized = toSignal(this.floatingPopupManagerStore.initialized$, {
    initialValue: false,
  });

  public activeTabLoading = toSignal(this.floatingPopupManagerStore.activeTabLoading$, {
    initialValue: false,
  });

  public activeTabIsChartFullscreen = toSignal(this.floatingPopupManagerStore.activeTabIsChartFullscreen$, {
    initialValue: false,
  });

  constructor() {
    super();

    const effectRef = effect(() => {
      const popupId = this.popupId();

      if (popupId) {
        this.initPopupStoreManager(popupId);

        effectRef.destroy();
      }
    });
  }

  public openReport(): void {
    this.dialogTrackerService.regAndOpen(DialogCreateLoggerNoiseReportComponent, {
      ...this.dialogTrackerService.getResponsiveDialogConfig('sm:!w-[26.8125rem]'),
      data: { loggerIds: [this.data.id] } satisfies IDialogCreateLoggerNoiseReportData,
    });
  }

  private initPopupStoreManager(popupId: string) {
    this.floatingPopupManagerStore.dispatch({
      type: FpLoggerNoiseManagerActionType.Init,
      popupId: popupId,
      selectedData: this.data,
      activeTab: this.floatingPopupTabs.getActive(popupId)() as FpLoggerNoiseTabs,
    });
  }
}
